/**
 * Mounts the example group's per-launch bookkeeping (core's
 * `useExampleGroupSession`) with web's language hook, query client and UI
 * store: counts this page load (the example rotates scenarios per launch)
 * and re-serves the example after a language switch — its detail pages
 * read the query cache, not the list memo. Mounted in `Providers` next to
 * `ApplyUserLanguage`.
 *
 * A page load that lands ON an example route does not count as a launch:
 * advancing would swap the group under the URL the user just reloaded,
 * and an expense id the next scenario lacks would 404.
 *
 * Also the bridge between the example's hide flag and the user's server
 * settings: the loaded profile's value is mirrored into the UI store, and
 * the API client is registered so a hide/show here reaches other devices.
 */
import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { EXAMPLE_GROUP_ID, useExampleGroupSession } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

const recordLaunch = () => useUIStore.getState().recordExampleGroupLaunch();
const shouldRecordLaunch = () =>
  !window.location.pathname.startsWith(`/groups/${EXAMPLE_GROUP_ID}`);
const applyServerHidden = (hidden: boolean) =>
  useUIStore.getState().applyServerExampleGroupHidden(hidden);
const registerPersister = (
  persist: ((hidden: boolean) => Promise<unknown>) | null
) => useUIStore.getState().setExampleGroupHiddenPersister(persist);

export function ExampleGroupSession(): null {
  const queryClient = useQueryClient();
  const { language } = useTranslation();
  const api = useTypedApi();
  // Once the user's settings are loaded, an unset flag means "shown":
  // leaving it undefined would keep the previous account's hide on this
  // device. Before the settings arrive (session established, profile not
  // yet fetched) the device value stays, so this user's own hide doesn't
  // flash off.
  const serverHidden = useAuthStore(state =>
    state.user?.settings
      ? (state.user.settings.exampleGroupHidden ?? false)
      : undefined
  );
  const persistHidden = useCallback(
    (hidden: boolean) => {
      // The auth store's user is persisted and is what `serverHidden` is
      // read from on the next page load, until /auth/me answers. Left at
      // the old value it would be mirrored back over the toggle.
      const { user, updateUser } = useAuthStore.getState();
      if (user) {
        updateUser({
          settings: { ...user.settings, exampleGroupHidden: hidden },
        });
      }
      return api.user.updateSettings({ exampleGroupHidden: hidden });
    },
    [api]
  );
  useExampleGroupSession({
    language,
    queryClient,
    recordLaunch,
    shouldRecordLaunch,
    serverHidden,
    applyServerHidden,
    persistHidden,
    registerPersister,
  });
  return null;
}
